import { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRight, BsCalendar, BsFileText, BsPeople } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import { useCompleteActivity, useDeleteActivity, useUpdateActivity } from '../../../models/activities/mutations';
import { useActivitiesForDealView } from '../../../models/activities/queries';
import { useChannelTypes } from '../../../models/channels/queries';
import { resolveChannelIcon } from '../../../utils/channelIcons';
import { isCallActivity } from '../../../utils/isCallActivity';

import ComposerCard from './ComposerCard';
import { renderActivityForm, resolveEditFormType } from './formRegistry';
import HistorySection from './HistorySection';
import PlannedSection from './PlannedSection';

const FIXED_TABS = [
	{ key: 'activity', label: 'Aktivität', icon: BsCalendar },
	{ key: 'note', label: 'Notiz', icon: BsFileText },
];

const CHANNEL_FORM_SLUGS = ['phone_manual', 'email', 'letter'];

const CHANNEL_TAB_LABEL = {
	phone_manual: 'Anruf',
	email: 'E-Mail',
	letter: 'Brief',
};

const ACTIVITY_TYPE_ICONS = {
	note: BsFileText,
	stage_change: BsArrowRight,
	meeting: BsPeople,
};

function iconForType(activity, channelType) {
	return ACTIVITY_TYPE_ICONS[activity.activityType] ?? resolveChannelIcon(channelType);
}

function filterHistory(activities, filter) {
	const completed = activities.filter((a) => a.status !== 'planned' && a.status !== 'in_progress');
	if (filter === 'all') return completed;
	if (filter === 'communication') {
		return completed.filter(
			(a) => a.activityType === 'communication' || a.activityType === 'task' || a.activityType === 'meeting',
		);
	}
	return completed.filter((a) => a.activityType === filter);
}

const CompanyActivityPanel = ({ companyId, dealId, contacts = [], companyEmail }) => {
	const [composerTab, setComposerTab] = useState(null);
	const [historyFilter, setHistoryFilter] = useState('all');
	const [historyMenuId, setHistoryMenuId] = useState(null);
	const [editingActivity, setEditingActivity] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const { data: allActivities = [], isLoading } = useActivitiesForDealView(companyId, dealId);
	const { data: channelTypes = [], isLoading: channelsLoading } = useChannelTypes(true);
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];

	const completeMutation = useCompleteActivity();
	const updateMutation = useUpdateActivity();
	const deleteMutation = useDeleteActivity(dealId, companyId);
	const showError = useErrorStore((s) => s.showError);

	const channelsBySlug = useMemo(() => Object.fromEntries(channelTypes.map((c) => [c.slug, c])), [channelTypes]);
	const channelsById = useMemo(() => Object.fromEntries(channelTypes.map((c) => [c.id, c])), [channelTypes]);

	const composerTabs = useMemo(() => {
		const allowedSlugs = new Set(CHANNEL_FORM_SLUGS);
		const channelTabs = [...channelTypes]
			.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
			.filter((ch) => allowedSlugs.has(ch.slug) && ch.isActive && ch.orgEnabled !== false)
			.map((ch) => ({
				key: ch.slug,
				label: CHANNEL_TAB_LABEL[ch.slug],
				icon: resolveChannelIcon(ch),
			}));
		return [...channelTabs, ...FIXED_TABS];
	}, [channelTypes]);

	const planned = useMemo(
		() =>
			allActivities
				.filter((a) => a.status === 'planned' || a.status === 'in_progress')
				.sort((a, b) => {
					// Sort by due date ascending (soonest/overdue first); items without a due date go last
					const aTime = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
					const bTime = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
					return aTime - bTime;
				}),
		[allActivities],
	);

	const history = useMemo(() => filterHistory(allActivities, historyFilter), [allActivities, historyFilter]);

	useEffect(() => {
		if (!historyMenuId) return;
		const handleEscape = (e) => {
			if (e.key === 'Escape') setHistoryMenuId(null);
		};
		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [historyMenuId]);

	const resolveChannel = useCallback(
		(a) => (a.channelTypeId ? (channelsById[a.channelTypeId] ?? null) : null),
		[channelsById],
	);

	const resolveIcon = useCallback((a) => iconForType(a, resolveChannel(a)), [resolveChannel]);

	const getChannelTypeId = useCallback((slug) => channelsBySlug[slug]?.id ?? null, [channelsBySlug]);

	const startEditing = useCallback((a) => {
		setEditingActivity(a);
		setHistoryMenuId(null);
	}, []);

	const closeComposer = useCallback(() => setComposerTab(null), []);

	const renderInlineEditForm = useCallback(
		(a) => {
			const formType = resolveEditFormType(a, resolveChannel);
			const formProps = {
				companyId,
				dealId,
				contacts,
				companyEmail,
				onClose: () => setEditingActivity(null),
				initialData: a,
			};
			return <Card className="p-0">{renderActivityForm({ formType, formProps, getChannelTypeId })}</Card>;
		},
		[companyId, dealId, contacts, companyEmail, getChannelTypeId, resolveChannel],
	);

	const composerFormProps = useMemo(
		() => ({ companyId, dealId, contacts, companyEmail, onClose: closeComposer }),
		[companyId, dealId, contacts, companyEmail, closeComposer],
	);

	// Mutation handlers

	const handleComplete = useCallback(
		async (a, reached) => {
			const payload = reached !== undefined && reached !== null ? { channelPayload: { reached } } : {};
			try {
				await completeMutation.mutateAsync({ id: a.id, payload });
			} catch {
				/* handled by global error toast */
			}
		},
		[completeMutation],
	);

	const handleCheckboxClick = useCallback((a) => handleComplete(a), [handleComplete]);

	const handleCallReachedSelect = useCallback((a, reached) => handleComplete(a, reached), [handleComplete]);

	const handleCancelAutomated = useCallback(
		async (a) => {
			try {
				await updateMutation.mutateAsync({ id: a.id, payload: { status: 'cancelled' } });
			} catch {
				/* handled */
			}
		},
		[updateMutation],
	);

	const handleReopen = useCallback(
		async (a) => {
			try {
				await updateMutation.mutateAsync({ id: a.id, payload: { status: 'planned', completedAt: null } });
			} catch {
				/* handled */
			}
		},
		[updateMutation],
	);

	const handleReassign = useCallback(
		async (activityId, userId) => {
			try {
				await updateMutation.mutateAsync({ id: activityId, payload: { userId } });
			} catch (err) {
				showError({ error: err, title: 'Zuweisung fehlgeschlagen' });
			}
		},
		[updateMutation, showError],
	);

	const handleDelete = useCallback((a) => setDeleteTarget(a), []);

	const confirmDelete = useCallback(async () => {
		if (!deleteTarget) return;
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
		} catch (err) {
			showError({ error: err, title: 'Löschen fehlgeschlagen' });
		}
	}, [deleteMutation, deleteTarget, showError]);

	const isCallType = useCallback((a) => isCallActivity(a, channelTypes), [channelTypes]);

	return (
		<div className="flex flex-col gap-5">
			<ComposerCard
				tabs={composerTabs}
				activeTab={composerTab}
				onTabChange={setComposerTab}
				channelsLoading={channelsLoading}
				formProps={composerFormProps}
				getChannelTypeId={getChannelTypeId}
			/>

			<div className="space-y-5">
				<PlannedSection
					planned={planned}
					editingActivityId={editingActivity?.id}
					renderInlineEditForm={renderInlineEditForm}
					resolveIcon={resolveIcon}
					isCallActivity={isCallType}
					members={members}
					onAddActivity={() => setComposerTab('activity')}
					onStartEdit={startEditing}
					onCheckboxClick={handleCheckboxClick}
					onCallReachedSelect={handleCallReachedSelect}
					onReassign={handleReassign}
					onCancelAutomated={handleCancelAutomated}
					onDelete={handleDelete}
				/>

				<HistorySection
					history={history}
					historyFilter={historyFilter}
					setHistoryFilter={setHistoryFilter}
					isLoading={isLoading}
					editingActivityId={editingActivity?.id}
					resolveChannel={resolveChannel}
					resolveIcon={resolveIcon}
					historyMenuId={historyMenuId}
					setHistoryMenuId={setHistoryMenuId}
					onEdit={startEditing}
					onReopen={handleReopen}
					onDelete={handleDelete}
					renderInlineEditForm={renderInlineEditForm}
				/>
			</div>

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={confirmDelete}
				title="Aktivität löschen"
				message={
					<>
						Möchten Sie die Aktivität <strong>&bdquo;{deleteTarget?.title}&ldquo;</strong> wirklich löschen?
					</>
				}
				confirmText="Löschen"
				isLoading={deleteMutation.isPending}
			/>
		</div>
	);
};

CompanyActivityPanel.propTypes = {
	companyId: PropTypes.string.isRequired,
	dealId: PropTypes.string,
	contacts: PropTypes.array,
	companyEmail: PropTypes.string,
};

export default CompanyActivityPanel;
